import type { ReactElement } from 'react'
import { cn } from '@/lib/cn'

export type ProgressTone = 'accent' | 'success' | 'warning' | 'danger'

export interface ProgressBarProps {
  /** 0–100. Clamped, so callers can pass raw ratios without guarding. */
  value: number
  indeterminate?: boolean
  tone?: ProgressTone
  label?: string
  className?: string
}

const tones: Record<ProgressTone, string> = {
  accent: 'bg-accent',
  success: 'bg-ok-500',
  warning: 'bg-warn-500',
  danger: 'bg-bad-500'
}

export function ProgressBar({
  value,
  indeterminate = false,
  tone = 'accent',
  label,
  className
}: ProgressBarProps): ReactElement {
  const pct = Math.min(100, Math.max(0, Math.round(Number.isFinite(value) ? value : 0)))

  return (
    <div className={cn('w-full', className)}>
      {label ? (
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          <span className="truncate text-xs font-medium text-fg-muted">{label}</span>
          {!indeterminate ? (
            <span className="shrink-0 text-xs tabular-nums text-fg-subtle">{pct}%</span>
          ) : null}
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : pct}
        className="relative h-2.5 w-full overflow-hidden rounded-full border border-line bg-sunken"
      >
        {indeterminate ? (
          // The keyframes drive both width and translation, so no width class here.
          <div className={cn('animate-indeterminate absolute inset-y-0 left-0 rounded-full', tones[tone])} />
        ) : (
          <div
            className={cn('h-full rounded-full transition-[width] duration-300 ease-out-quart', tones[tone])}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  )
}
